import { environment } from '../../environments/environment';

export interface SEOConfig {
  title: string;
  description: string;
  keywords: string;
  image: string;
  url: string;
  structuredData: any;
}

export const baseUrl = environment.baseUrl;
export const defaultImage = `${baseUrl}/assets/images/og-image.png`;
export const organizationStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: environment.company.name,
  alternateName: environment.company.displayName,
  url: baseUrl,
  logo: `${baseUrl}/public/assets/logo.png`,
  description: environment.company.description,
  address: {
    '@type': 'PostalAddress',
    streetAddress: environment.company.address.street,
    addressCountry: environment.company.address.country,
    addressRegion: environment.company.address.state,
    addressLocality: environment.company.address.city,
    postalCode: environment.company.address.postalCode,
  },
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: environment.contact.phoneDisplay,
    contactType: 'Customer Service',
    availableLanguage: ['English', 'Tamil'],
  },
  sameAs: [
    environment.socialMedia.twitter,
    environment.socialMedia.linkedin,
    environment.socialMedia.facebook,
    environment.socialMedia.instagram,
  ],
};

export const seoConfig: Record<string, SEOConfig> = {
  home: {
    title:
      'Digital vCards & Digital Visiting Cards | DigiVcards.ai',
    description:
      'Create and share your professional Digital vCard instantly with DigiVcards.ai. Easy to use, mobile-friendly, and perfect for businesses.',
    keywords:
      'Digital vCard, Digital vCards, Digital Visiting Card, Digital Visiting Cards, create digital vCard, make digital visiting card, build professional vCard, design digital business card, online visiting card, virtual visiting card, professional vCard maker, electronic visiting card, business vCard creator, generate digital card, custom vCard builder',
    image: defaultImage,
    url: baseUrl,
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'DiGivCards - Digital vCards Online',
      url: baseUrl,
      description:
        'Professional digital vCard platform for creating and sharing business cards online',
      publisher: organizationStructuredData,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${baseUrl}/search?q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    },
  },

  features: {
    title: 'Features - Digital Visiting Card Platform | DiGivCards',
    description:
      'Explore powerful features of our digital visiting cards: NFC technology, real-time analytics, custom designs, QR codes, and seamless sharing capabilities.',
    keywords:
      'digital card features, NFC technology, business card analytics, custom card design, QR code cards, contactless sharing',
    image: `${baseUrl}/assets/images/features-og.jpg`,
    url: `${baseUrl}/features`,
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: 'DiGivCards Digital Visiting Card Platform',
      description:
        'Professional digital visiting card solution with advanced features',
      brand: organizationStructuredData,
      offers: {
        '@type': 'Offer',
        price: environment.pricing.basicPlan.price.toString(),
        priceCurrency: environment.pricing.basicPlan.currency,
        availability: 'https://schema.org/InStock',
        priceValidUntil: '2025-12-31',
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.8',
        reviewCount: '500',
        bestRating: '5',
        worstRating: '1',
      },
    },
  },

  benefits: {
    title: 'Business Benefits - Why Choose Digital Visiting Cards | DiGivCards',
    description:
      'Discover the business benefits of digital visiting cards: cost savings, environmental impact, enhanced networking, real-time updates, and professional image.',
    keywords:
      'digital card benefits, business networking, cost-effective cards, eco-friendly business, professional networking, paperless cards',
    image: `${baseUrl}/assets/images/benefits-og.jpg`,
    url: `${baseUrl}/benefits`,
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: 'Business Benefits of Digital Visiting Cards',
      description:
        'Comprehensive guide to the advantages of switching to digital visiting cards',
      author: organizationStructuredData,
      publisher: organizationStructuredData,
      datePublished: '2024-01-01',
      dateModified: '2025-08-12',
      mainEntityOfPage: `${baseUrl}/benefits`,
    },
  },

  'roi-calculator': {
    title: 'ROI Calculator - Calculate Your Digital Card Savings | DiGivCards',
    description:
      'Calculate your return on investment with our digital visiting cards. See how much you can save on printing costs while improving your networking efficiency.',
    keywords:
      'ROI calculator, digital card savings, business card cost, printing cost calculator, investment return, cost-benefit analysis',
    image: `${baseUrl}/assets/images/roi-calculator-og.jpg`,
    url: `${baseUrl}/roi-calculator`,
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: 'Digital Card ROI Calculator',
      description:
        'Calculate potential savings and return on investment for digital visiting cards',
      url: `${baseUrl}/roi-calculator`,
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web Browser',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'INR',
      },
    },
  },

  comparison: {
    title: 'Digital vs Traditional Cards Comparison | DiGivCards',
    description:
      'Compare digital visiting cards with traditional paper cards. See the advantages in cost, sustainability, features, and professional impact.',
    keywords:
      'digital vs paper cards, card comparison, traditional vs digital, business card evolution, sustainable networking',
    image: `${baseUrl}/assets/images/comparison-og.jpg`,
    url: `${baseUrl}/comparison`,
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'ComparisonTable',
      name: 'Digital vs Traditional Business Cards',
      description:
        'Detailed comparison between digital and traditional business cards',
      about: [
        {
          '@type': 'Thing',
          name: 'Digital Business Cards',
        },
        {
          '@type': 'Thing',
          name: 'Traditional Paper Cards',
        },
      ],
    },
  },

  testimonials: {
    title: 'Customer Reviews & Success Stories | DiGivCards',
    description:
      'Read genuine customer reviews and success stories from businesses using our digital visiting cards. See why professionals choose DiGivCards.',
    keywords:
      'customer reviews, testimonials, success stories, user feedback, business networking stories, digital card reviews',
    image: `${baseUrl}/assets/images/testimonials-og.jpg`,
    url: `${baseUrl}/testimonials`,
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'Review',
      itemReviewed: {
        '@type': 'Product',
        name: 'DiGivCards Digital Visiting Cards',
      },
      reviewRating: {
        '@type': 'Rating',
        ratingValue: '4.8',
        bestRating: '5',
      },
      author: {
        '@type': 'Organization',
        name: 'DiGivCards Users',
      },
      reviewBody:
        'Customers love our digital visiting card platform for its ease of use and professional features.',
    },
  },

  pricing: {
    title: 'Pricing Plans - Affordable Digital Visiting Cards | DiGivCards',
    description:
      'Choose from our flexible pricing plans for digital visiting cards. Simple one-time payment of ₹3999/- + GST per card. No hidden fees.',
    keywords:
      'digital card pricing, business card plans, affordable digital cards, pricing packages, cost-effective networking',
    image: `${baseUrl}/assets/images/pricing-og.jpg`,
    url: `${baseUrl}/pricing`,
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'PriceSpecification',
      price: environment.pricing.basicPlan.price.toString(),
      priceCurrency: environment.pricing.basicPlan.currency,
      name: 'Digital Visiting Card',
      description:
        'Professional digital visiting card with all features included',
      validFrom: '2024-01-01',
      validThrough: '2025-12-31',
    },
  },

  faq: {
    title: 'Frequently Asked Questions | DiGivCards Support',
    description:
      'Find answers to common questions about digital visiting cards, NFC technology, pricing, features, and how to get started with DiGivCards.',
    keywords:
      'FAQ, frequently asked questions, digital card help, NFC cards questions, support, help center',
    image: `${baseUrl}/assets/images/faq-og.jpg`,
    url: `${baseUrl}/faq`,
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What are digital visiting cards?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Digital visiting cards are modern, contactless business cards that can be shared instantly via NFC, QR codes, or links.',
          },
        },
        {
          '@type': 'Question',
          name: 'How much do digital cards cost?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: `Our digital visiting cards cost ${environment.pricing.basicPlan.currencySymbol}${environment.pricing.basicPlan.price}/- + GST per card with one-time payment.`,
          },
        },
        {
          '@type': 'Question',
          name: 'Do I need special hardware for NFC cards?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Most modern smartphones support NFC technology. No additional hardware is required for recipients to view your digital card.',
          },
        },
      ],
    },
  },

  contact: {
    title: 'Contact Us - Get in Touch | DiGivCards Support',
    description:
      'Contact our support team for help with digital visiting cards. Get assistance with setup, features, pricing, or technical questions.',
    keywords:
      'contact support, customer service, help desk, technical support, get in touch, customer care',
    image: `${baseUrl}/assets/images/contact-og.jpg`,
    url: `${baseUrl}/contact`,
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      mainEntity: {
        '@type': 'Organization',
        name: 'DiGivCards Support',
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: environment.contact.phoneDisplay,
          contactType: 'Customer Support',
          email: environment.contact.supportEmail,
          availableLanguage: ['English', 'Tamil'],
        },
      },
    },
  },

  'get-started': {
    title: 'Get Started with Digital Visiting Cards | Create Your Card Today',
    description:
      'Ready to create your digital visiting card? Choose your platform, download our app, or get started today. Multiple options to begin with DiGivCards!',
    keywords:
      'get started, create digital card, sign up, download app, onboarding, start now, register',
    image: `${baseUrl}/assets/images/get-started-og.jpg`,
    url: `${baseUrl}/get-started`,
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Get Started with DiGivCards',
      description:
        'Choose your preferred way to create and manage your digital visiting cards',
      url: `${baseUrl}/get-started`,
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: baseUrl,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Get Started',
            item: `${baseUrl}/get-started`,
          },
        ],
      },
      mainEntity: {
        '@type': 'SoftwareApplication',
        name: 'DiGivCards',
        applicationCategory: 'BusinessApplication',
        operatingSystem: ['iOS', 'Android', 'Web'],
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: environment.pricing.basicPlan.currency,
          description: `One-time payment`,
        },
      },
    },
  },

  payment: {
    title: 'Secure Payment - Complete Your Digital Card Purchase | DiGivCards',
    description:
      'Complete your secure payment for digital visiting card. Professional features, lifetime access, one-time payment. 100% secure checkout with multiple payment options.',
    keywords:
      'secure payment, buy digital card, checkout, payment gateway, safe transaction, digital card purchase, business card payment',
    image: `${baseUrl}/assets/images/payment-og.jpg`,
    url: `${baseUrl}/payment`,
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'PaymentChargeSpecification',
      price: environment.pricing.basicPlan.price.toString(),
      priceCurrency: environment.pricing.basicPlan.currency,
      name: 'Digital Visiting Card Purchase',
      description:
        'Secure payment for professional digital visiting card with all features',
      validFrom: '2024-01-01',
      validThrough: '2025-12-31',
      eligibleTransactionVolume: {
        '@type': 'PriceSpecification',
        price: environment.pricing.basicPlan.price.toString(),
        priceCurrency: environment.pricing.basicPlan.currency,
      },
      appliesToPaymentMethod: [
        'http://purl.org/goodrelations/v1#ByBankTransferInAdvance',
        'http://purl.org/goodrelations/v1#ByInvoice',
        'http://purl.org/goodrelations/v1#PayPal',
      ],
    },
  },

  cashback: {
    title: 'Cashback Programs - Earn Money by Sharing | DiGivCards Affiliate',
    description:
      'Join our exclusive cashback programs and earn up to 30% commission! Turn your network into passive income. Referral program (10%) and Agency partnership (30%) available.',
    keywords:
      'cashback program, affiliate program, referral earnings, passive income, agency partnership, earn money, commission, network marketing, digital card referrals',
    image: `${baseUrl}/assets/images/cashback-og.jpg`,
    url: `${baseUrl}/cashback`,
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'DiGivCards Cashback Programs',
      description:
        'Earn money by sharing DiGivCards with your network through our cashback and affiliate programs',
      url: `${baseUrl}/cashback`,
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: baseUrl,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Cashback Programs',
            item: `${baseUrl}/cashback`,
          },
        ],
      },
      mainEntity: [
        {
          '@type': 'Offer',
          name: 'Referral Cashback Program',
          description: 'Earn 10% cashback for every successful referral',
          price: '10',
          priceCurrency: 'Percentage',
          availability: 'https://schema.org/InStock',
          seller: organizationStructuredData,
        },
        {
          '@type': 'Offer',
          name: 'Agency Partnership Program',
          description: 'Earn 30% commission as an agency partner',
          price: '30',
          priceCurrency: 'Percentage',
          availability: 'https://schema.org/InStock',
          seller: organizationStructuredData,
        },
      ],
      potentialAction: [
        {
          '@type': 'JoinAction',
          name: 'Join Referral Program',
          target: `${baseUrl}/cashback#referral`,
        },
        {
          '@type': 'JoinAction',
          name: 'Become Agency Partner',
          target: `${baseUrl}/cashback#agency`,
        },
      ],
    },
  },
};
